import { useGetCurrentUserQuery, useSetUserPreferenceMutation } from '@generated/graphql';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import {
  ServerLaunchOption,
  UserPreferences,
  hasSeenAgentStudioGettingStartedAtom,
  hasSeenGettingStartedAtom,
  hiddenOrganizationIdsAtom,
  isOrganizationHiddenAtom,
  preferencesLoadedAtom,
  preferredIDEAtom,
  themePreferenceAtom,
  userPreferencesAtom,
} from './userPreferencesAtoms';

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useAtom(userPreferencesAtom);
  const [hiddenOrganizationIds] = useAtom(hiddenOrganizationIdsAtom);
  const [isOrganizationHidden] = useAtom(isOrganizationHiddenAtom);
  const [themePreference] = useAtom(themePreferenceAtom);
  const [preferredIDE] = useAtom(preferredIDEAtom);
  const [hasSeenGettingStarted] = useAtom(hasSeenGettingStartedAtom);
  const [hasSeenAgentStudioGettingStarted] = useAtom(hasSeenAgentStudioGettingStartedAtom);
  const preferencesLoaded = useAtomValue(preferencesLoadedAtom);
  const setPreferencesLoaded = useSetAtom(preferencesLoadedAtom);

  const { data: userData, loading: userLoading } = useGetCurrentUserQuery();
  const [setUserPreferenceMutation] = useSetUserPreferenceMutation({
    refetchQueries: ['GetCurrentUser'],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!userLoading && userData?.getCurrentUser) {
      const preference = userData.getCurrentUser.preference || {};
      setPreferences(preference);
      setPreferencesLoaded(true);
    }
  }, [userData, userLoading, setPreferences, setPreferencesLoaded]);

  const updatePreferences = useCallback(
    async (newPreferences: UserPreferences) => {
      setPreferences(newPreferences);

      try {
        await setUserPreferenceMutation({
          variables: {
            preference: JSON.stringify(newPreferences),
          },
        });
      } catch (error) {
        console.error('Failed to update user preferences:', error);
        setPreferences(preferences);
        throw error;
      }
    },
    [preferences, setPreferences, setUserPreferenceMutation],
  );

  const toggleOrganizationVisibility = useCallback(
    (organizationId: string) => {
      const currentHidden = preferences.hiddenOrganizationIds || [];
      const newHidden = currentHidden.includes(organizationId)
        ? currentHidden.filter((id) => id !== organizationId)
        : [...currentHidden, organizationId];

      const newPreferences: UserPreferences = {
        ...preferences,
        hiddenOrganizationIds: newHidden,
      };

      return updatePreferences(newPreferences);
    },
    [preferences, updatePreferences],
  );

  const setTheme = useCallback(
    (theme: 'light' | 'dark') => {
      const newPreferences: UserPreferences = {
        ...preferences,
        theme,
      };

      return updatePreferences(newPreferences);
    },
    [preferences, updatePreferences],
  );

  const setPreferredIDE = useCallback(
    (newPreferredIDE: ServerLaunchOption) => {
      const newPreferences: UserPreferences = {
        ...preferences,
        preferredIDE: newPreferredIDE,
      };

      return updatePreferences(newPreferences);
    },
    [preferences, updatePreferences],
  );

  const markGettingStartedAsSeen = useCallback(() => {
    const newPreferences: UserPreferences = {
      ...preferences,
      hasSeenGettingStarted: true,
    };

    return updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  const markAgentStudioGettingStartedAsSeen = useCallback(() => {
    const newPreferences: UserPreferences = {
      ...preferences,
      hasSeenAgentStudioGettingStarted: true,
    };

    return updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  return {
    preferences,
    preferencesLoaded,
    userLoading,
    hiddenOrganizationIds,
    isOrganizationHidden,
    themePreference,
    preferredIDE,
    hasSeenGettingStarted,
    hasSeenAgentStudioGettingStarted,
    toggleOrganizationVisibility,
    setTheme,
    markGettingStartedAsSeen,
    markAgentStudioGettingStartedAsSeen,
    updatePreferences,
    setPreferredIDE,
  };
};
